import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, EyeOff, Eye } from "lucide-react";
import {
  useAllClinicAppointmentTypes,
  useCreateClinicAppointmentType,
  useUpdateClinicAppointmentType,
} from "@/hooks/useClinicAppointmentTypes";
import type { ClinicAppointmentType, AppointmentTypeBehavior } from "@/hooks/useClinicAppointmentTypes";
import { Button } from "@akuna/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { APPOINTMENT_ICON_OPTIONS, renderIconBySlug } from "@/lib/event-icons";
import { BEHAVIOR_OPTIONS, BEHAVIOR_LABEL_KEYS } from "@/lib/appointments/availability";
import { toast } from "sonner";

type FormState = {
  name: string;
  color: string;
  icon: string;
  behavior: AppointmentTypeBehavior;
};

const DEFAULT_FORM: FormState = {
  name: "",
  color: "#6366f1",
  icon: "clipboard-plus",
  behavior: "padrao",
};

export function ClinicAppointmentTypesSettings() {
  const { t } = useTranslation(["settings", "calendar", "common"]);
  const { data: types = [], isLoading } = useAllClinicAppointmentTypes();
  const createMutation = useCreateClinicAppointmentType();
  const updateMutation = useUpdateClinicAppointmentType();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ClinicAppointmentType | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);

  function openCreate() {
    setEditing(null);
    setForm(DEFAULT_FORM);
    setDialogOpen(true);
  }

  function openEdit(type: ClinicAppointmentType) {
    setEditing(type);
    setForm({ name: type.name, color: type.color, icon: type.icon, behavior: type.behavior });
    setDialogOpen(true);
  }

  async function handleSave() {
    const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;
    if (!form.name.trim()) return;
    if (!hexColorRegex.test(form.color)) {
      toast.error(t("settings:appointmentTypes.saveError"));
      return;
    }
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, ...form });
        toast.success(t("settings:appointmentTypes.updateSuccess"));
      } else {
        await createMutation.mutateAsync(form);
        toast.success(t("settings:appointmentTypes.createSuccess"));
      }
      setDialogOpen(false);
    } catch {
      toast.error(t("settings:appointmentTypes.saveError"));
    }
  }

  async function handleToggleActive(type: ClinicAppointmentType) {
    try {
      await updateMutation.mutateAsync({ id: type.id, active: !type.active });
      toast.success(
        type.active
          ? t("settings:appointmentTypes.deactivated")
          : t("settings:appointmentTypes.activated"),
      );
    } catch {
      toast.error(t("settings:appointmentTypes.saveError"));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t("settings:appointmentTypes.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("settings:appointmentTypes.description")}</p>
        </div>
        <Button onClick={openCreate} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          {t("settings:appointmentTypes.addType")}
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t("common:loading")}</p>
      ) : types.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
          <p>{t("settings:appointmentTypes.empty")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {types.map((type) => (
            <div
              key={type.id}
              className={`flex items-center justify-between rounded-lg border p-3 transition-opacity ${!type.active ? "opacity-50" : ""}`}
            >
              <div className="flex items-center gap-3">
                <span
                  className="flex h-8 w-8 items-center justify-center rounded-full text-white flex-shrink-0"
                  style={{ backgroundColor: type.color }}
                >
                  {renderIconBySlug(type.icon, { sizeClass: "h-4 w-4", tone: "white" })}
                </span>
                <div>
                  <p className="font-medium text-sm">{type.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {t(`calendar:${BEHAVIOR_LABEL_KEYS[type.behavior]}`)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => openEdit(type)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleToggleActive(type)}>
                  {type.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing
                ? t("settings:appointmentTypes.editTitle")
                : t("settings:appointmentTypes.createTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="apt-name">{t("settings:appointmentTypes.nameLabel")}</Label>
              <Input
                id="apt-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t("settings:appointmentTypes.namePlaceholder")}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings:appointmentTypes.behaviorLabel")}</Label>
              <Select
                value={form.behavior}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, behavior: v as AppointmentTypeBehavior }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BEHAVIOR_OPTIONS.map(({ value, labelKey }) => (
                    <SelectItem key={value} value={value}>
                      {t(`calendar:${labelKey}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings:appointmentTypes.colorLabel")}</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  className="h-9 w-12 cursor-pointer rounded border p-0.5"
                />
                <Input
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  placeholder="#6366f1"
                  className="font-mono"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings:appointmentTypes.iconLabel")}</Label>
              <div className="grid max-h-64 grid-cols-2 gap-2 overflow-y-auto pr-1 sm:grid-cols-3">
                {APPOINTMENT_ICON_OPTIONS.map(({ slug, labelKey }) => {
                  const isSelected = form.icon === slug;
                  const label = t(labelKey);
                  return (
                    <button
                      key={slug}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, icon: slug }))}
                      className={`flex min-h-10 items-center gap-2 rounded-md border px-2 py-2 text-left text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                        isSelected ? "border-primary bg-primary/10 text-primary" : "border-border"
                      }`}
                      aria-label={label}
                      aria-pressed={isSelected}
                      title={label}
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-muted">
                        {renderIconBySlug(slug, { sizeClass: "h-4 w-4", tone: "current" })}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t("common:cancel")}
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                !form.name.trim() ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {t("common:save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
